import React, { useEffect, useRef, useState } from 'react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useSetting } from '../utils/AppSettingsContext';
import { getTargets, createTarget, updateTarget } from '../api/targetsAPI';
import { getClass } from '../api/classAPI';
import { getCategory } from '../api/categoryAPI';
import {
  getCategoryTargets,
  createCategoryTarget,
  updateCategoryTarget,
} from '../api/category-targetsAPI';
import $ from 'jquery';
import { Icon } from '@iconify/react';

const formatPercentage = value =>
  value !== null && value !== undefined ? `${(value * 100).toFixed(0)}%` : '';

const isKpiIncluded = value => value !== false && value !== 0;

const CategoryTargetsSection = ({ userProfile, disabled = false }) => {
  const [targets, setTargets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newTarget, setNewTarget] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchTargets = async () => {
    const data = await getCategoryTargets();
    setTargets(data);
  };

  const fetchCategories = async () => {
    const data = await getCategory();
    setCategories(data);
  };

  useEffect(() => {
    fetchTargets();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (targets.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({ pageLength: 25, dom: 'rtp' });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [targets]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setNewTarget(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newTarget.categoryId) newErrors.categoryId = 'Category is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const payload = {
      categoryId: Number(newTarget.categoryId),
      availability:
        newTarget.availability !== undefined && newTarget.availability !== ''
          ? Number(newTarget.availability) / 100
          : null,
      utilisation:
        newTarget.utilisation !== undefined && newTarget.utilisation !== ''
          ? Number(newTarget.utilisation) / 100
          : null,
      mtbf: newTarget.mtbf ? Number(newTarget.mtbf) : null,
      mttr: newTarget.mttr ? Number(newTarget.mttr) : null,
      includeInKpis:
        newTarget.includeInKpis !== undefined ? newTarget.includeInKpis : undefined,
    };
    if (editingId) {
      await updateCategoryTarget(editingId, payload);
    } else {
      await createCategoryTarget(payload);
    }
    setShowModal(false);
    setEditingId(null);
    setNewTarget({});
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    await fetchTargets();
  };

  const handleToggleInclude = async item => {
    const newValue = !isKpiIncluded(item.includeInKpis);
    if (item.id) {
      await updateCategoryTarget(item.id, { includeInKpis: newValue });
    } else {
      await createCategoryTarget({ categoryId: item.categoryId, includeInKpis: newValue });
    }
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    await fetchTargets();
  };

  const getCategoryName = id =>
    categories.find(c => c.id === id)?.categoryName || '';

  return (
    <div className={`card h-100 p-0 rounded-0 ${disabled ? 'opacity-50' : ''}`}> 
      <div className="card-header d-flex justify-content-between p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          disabled={disabled}
        />
      </div>
      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Category</th>
                <th>Include in KPIs?</th>
                <th className="text-start">Availability (%)</th>
                <th className="text-start">Utilisation (%)</th>
                <th className="text-start">MTBF</th>
                <th className="text-start">MTTR</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {targets.map(item => (
                <tr key={item.id ?? item.categoryId}>
                  <td><strong>{getCategoryName(item.categoryId)}</strong></td>
                  <td>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={isKpiIncluded(item.includeInKpis)}
                        onChange={() => handleToggleInclude(item)}
                        disabled={disabled}
                      />
                      <label className="form-check-label line-height-1 fw-medium text-secondary-light">
                        {isKpiIncluded(item.includeInKpis) ? 'Yes' : 'No'}
                      </label>
                    </div>
                  </td>
                  <td className="text-start">{formatPercentage(item.availability)}</td>
                  <td className="text-start">{formatPercentage(item.utilisation)}</td>
                  <td className="text-start">{item.mtbf ?? ''}</td>
                  <td className="text-start">{item.mttr ?? ''}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('targets:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => {
                            setNewTarget({
                              ...item,
                              availability:
                                item.availability !== null && item.availability !== undefined
                                  ? item.availability * 100
                                  : '',
                              utilisation:
                                item.utilisation !== null && item.utilisation !== undefined
                                  ? item.utilisation * 100
                                  : '',
                              includeInKpis: isKpiIncluded(item.includeInKpis),
                            });
                            setEditingId(item.id);
                            setShowModal(true);
                          }}
                          disabled={disabled}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {!disabled && showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Target' : 'New Target'}</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Category *</label>
                  <select
                    name="categoryId"
                    className={`form-select ${errors.categoryId ? 'is-invalid' : ''}`}
                    value={newTarget.categoryId || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Category</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.categoryName}</option>
                    ))}
                  </select>
                  {errors.categoryId && <div className="invalid-feedback">{errors.categoryId}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Availability (%)</label>
                  <input
                    type="number"
                    name="availability"
                    className="form-control"
                    min="0"
                    max="100"
                    step="1"
                    value={newTarget.availability || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Utilisation (%)</label>
                  <input
                    type="number"
                    name="utilisation"
                    className="form-control"
                    min="0"
                    max="100"
                    step="1"
                    value={newTarget.utilisation || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">MTBF</label>
                  <input
                    type="number"
                    name="mtbf"
                    className="form-control"
                    value={newTarget.mtbf || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">MTTR</label>
                  <input
                    type="number"
                    name="mttr"
                    className="form-control"
                    value={newTarget.mttr || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Include in KPIs?</label>
                  <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      name="includeInKpis"
                      checked={isKpiIncluded(newTarget.includeInKpis)}
                      onChange={e =>
                        setNewTarget(prev => ({ ...prev, includeInKpis: e.target.checked }))
                      }
                    />
                    <label className="form-check-label line-height-1 fw-medium text-secondary-light">
                      {isKpiIncluded(newTarget.includeInKpis) ? 'Yes' : 'No'}
                    </label>
                </div>
              </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ClassTargetsSection = ({ userProfile }) => {
  const [targets, setTargets] = useState([]);
  const [classList, setClassList] = useState([]);
  const [newTarget, setNewTarget] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchTargets = async () => {
    const data = await getTargets();
    setTargets(data);
  };

  const fetchClasses = async () => {
    const data = await getClass();
    setClassList(data);
  };

  useEffect(() => {
    fetchTargets();
    fetchClasses();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (targets.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({ pageLength: 25, dom: 'rtp' });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [targets]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setNewTarget(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newTarget.classId) newErrors.classId = 'Class is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const payload = {
      classId: Number(newTarget.classId),
      availability:
        newTarget.availability !== undefined && newTarget.availability !== ''
          ? Number(newTarget.availability) / 100
          : null,
      utilisation:
        newTarget.utilisation !== undefined && newTarget.utilisation !== ''
          ? Number(newTarget.utilisation) / 100
          : null,
      mtbf: newTarget.mtbf ? Number(newTarget.mtbf) : null,
      mttr: newTarget.mttr ? Number(newTarget.mttr) : null,
      includeInKpis:
        newTarget.includeInKpis !== undefined ? newTarget.includeInKpis : undefined,
    };
    if (editingId) {
      await updateTarget(editingId, payload);
    } else {
      await createTarget(payload);
    }
    setShowModal(false);
    setEditingId(null);
    setNewTarget({});
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    await fetchTargets();
  };

  const handleToggleInclude = async item => {
    const newValue = !isKpiIncluded(item.includeInKpis);
    if (item.id) {
      await updateTarget(item.id, { includeInKpis: newValue });
    } else {
      await createTarget({ classId: item.classId, includeInKpis: newValue });
    }
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    await fetchTargets();
  };

  const getClassName = id => classList.find(c => c.id === id)?.className || '';

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Class</th>
                <th>Include in KPIs?</th>
                <th className="text-start">Availability (%)</th>
                <th className="text-start">Utilisation (%)</th>
                <th className="text-start">MTBF</th>
                <th className="text-start">MTTR</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {targets.map((item, index) => (
                <tr key={item.id ?? `${item.classId}-${index}`}>
                  <td><strong>{getClassName(item.classId)}</strong></td>
                  <td>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={isKpiIncluded(item.includeInKpis)}
                        onChange={() => handleToggleInclude(item)}
                      />
                      <label className="form-check-label line-height-1 fw-medium text-secondary-light">
                        {isKpiIncluded(item.includeInKpis) ? 'Yes' : 'No'}
                      </label>
                    </div>
                  </td>
                  <td className="text-start">{formatPercentage(item.availability)}</td>
                  <td className="text-start">{formatPercentage(item.utilisation)}</td>
                  <td className="text-start">{item.mtbf ?? ''}</td>
                  <td className="text-start">{item.mttr ?? ''}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('targets:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => {
                            setNewTarget({
                              ...item,
                              availability:
                                item.availability !== null && item.availability !== undefined
                                  ? item.availability * 100
                                  : '',
                              utilisation:
                                item.utilisation !== null && item.utilisation !== undefined
                                  ? item.utilisation * 100
                                  : '',
                              includeInKpis: isKpiIncluded(item.includeInKpis),
                            });
                            setEditingId(item.id);
                            setShowModal(true);
                          }}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Target' : 'New Target'}</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Class *</label>
                  <select
                    name="classId"
                    className={`form-select ${errors.classId ? 'is-invalid' : ''}`}
                    value={newTarget.classId || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Class</option>
                    {classList.map(c => (
                      <option key={c.id} value={c.id}>{c.className}</option>
                    ))}
                  </select>
                  {errors.classId && <div className="invalid-feedback">{errors.classId}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Availability (%)</label>
                  <input
                    type="number"
                    name="availability"
                    className="form-control"
                    min="0"
                    max="100"
                    step="1"
                    value={newTarget.availability || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Utilisation (%)</label>
                  <input
                    type="number"
                    name="utilisation"
                    className="form-control"
                    min="0"
                    max="100"
                    step="1"
                    value={newTarget.utilisation || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">MTBF</label>
                  <input
                    type="number"
                    name="mtbf"
                    className="form-control"
                    value={newTarget.mtbf || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">MTTR</label>
                  <input
                    type="number"
                    name="mttr"
                    className="form-control"
                    value={newTarget.mttr || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Include in KPIs?</label>
                  <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      name="includeInKpis"
                      checked={isKpiIncluded(newTarget.includeInKpis)}
                      onChange={e =>
                        setNewTarget(prev => ({ ...prev, includeInKpis: e.target.checked }))
                      }
                    />
                    <label className="form-check-label line-height-1 fw-medium text-secondary-light">
                      {isKpiIncluded(newTarget.includeInKpis) ? 'Yes' : 'No'}
                    </label>
                </div>
              </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TargetsLayer = () => {
  const userProfile = useUserProfile();
  const showAssetCategory = useSetting('show_asset_category');

  const categoryExpanded = showAssetCategory;
  const classExpanded = !showAssetCategory;

  return (
    <div className="accordion" id="targetsAccordion">
      <div className="accordion-item">
        <h2 className="accordion-header" id="headingCategory">
          <button
            className={`accordion-button ${categoryExpanded ? '' : 'collapsed'}`}
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#collapseCategory"
            aria-expanded={categoryExpanded}
            aria-controls="collapseCategory"
          >
            Category Targets
          </button>
        </h2>
        <div
          id="collapseCategory"
          className={`accordion-collapse collapse ${categoryExpanded ? 'show' : ''}`}
          aria-labelledby="headingCategory"
          data-bs-parent="#targetsAccordion"
        >
          <div className="accordion-body p-0">
            <CategoryTargetsSection
              userProfile={userProfile}
              disabled={!showAssetCategory}
            />
          </div>
        </div>
      </div>
      <div className="accordion-item">
        <h2 className="accordion-header" id="headingClass">
          <button
            className={`accordion-button ${classExpanded ? '' : 'collapsed'}`}
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#collapseClass"
            aria-expanded={classExpanded}
            aria-controls="collapseClass"
          >
            Class Targets
          </button>
        </h2>
        <div
          id="collapseClass"
          className={`accordion-collapse collapse ${classExpanded ? 'show' : ''}`}
          aria-labelledby="headingClass"
          data-bs-parent="#targetsAccordion"
        >
          <div className="accordion-body p-0">
            <ClassTargetsSection userProfile={userProfile} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetsLayer;
